// Copyright 2023 Autodesk, Inc.
// All rights reserved.
// This computer source code and related instructions and comments are the
// unpublished confidential and proprietary information of Autodesk, Inc.
// and are protected under Federal copyright and state trade secret law.
// They may not be disclosed to, copied or used by any third party without
// the prior written consent of Autodesk, Inc.


import {DtConstants} from "../schema/DtConstants";
import {ElementFlags, KeyFlags} from "../schema/dt-schema";
import * as dte from '../DtEventTypes';
import {
	AllFacetsScopes,
	FacetsScope,
	FacetTypes,
	getFacetListForScope,
} from "./Facets";

import {FacetsForScope} from "./FacetsForScope";
import {FacetVizEffects} from "./FacetVizEffects";
import {DtPrefs3D} from "../preferences/DtPreferenceNames";
import {compareNumbers, sortedArrayIntersection, sortedArrayHas, sortedArrayUnion} from "./sortedArray";
const RC = require("../resources/cats_enum.json");

import { av } from "@adsk/lmv";

// Models is an immovable facet, and it is kept at this index.
export const MODEL_FACET_DEFS_IDX = 0;

function smallArraysEqual(a, b) {
	//Definitely not a fast or robust implementation -- this only expects arrays with up to 3 elements
	//as input.
	if (!a) a = [];
	if (!b) b = [];

	return a.every(item => b.includes(item)) && b.every(item => a.includes(item));
}


export class FacetsManager {

	/**
	 * @param {Object.<string, any>} options Application configuration options
	 */
	constructor(facility, options = {}, wantClassificationFacet) {
		this.facility = facility;
		this.models = [];

		this.options = options;
		//this.options.useTopLevel = true;

		this.facetsEffects = new FacetVizEffects(this.facility, this.options);

		const scopes = Object.values(FacetsScope);
		// note: close to a no-op when no elements, but could skip pre-emptively.
		this.facetsForScope = scopes
			.map((scope) => new FacetsForScope(this, scope, options, getFacetListForScope(scope, facility, wantClassificationFacet)));

		this.hiddenCategories = [];//or optionally [RC.Rooms, RC.MEPSpaces, RC.Levels], set via the Edit Filters UI

		this.boundOnIsolationChanged = this.onIsolationChanged.bind(this);
		this.boundOnDocumentsChanged = this.onDocumentsChanged.bind(this);
		this.boundOnSystemsChanged = this.onSystemsChanged.bind(this);
		this.boundOnPreferenceChanged = this.onPreferenceChanged.bind(this);
		this.boundOnRoomFragmentLoaded = this.onRoomFragmentLoaded.bind(this);

		this.viewRestoreInProgress = false;

		this.setEventTarget(this.facility.eventTarget);
		this.eventTarget.addEventListener(dte.DT_DOCUMENTS_CHANGED_EVENT, this.boundOnDocumentsChanged);
		this.eventTarget.addEventListener(dte.DT_SYSTEMS_CHANGED_EVENT, this.boundOnSystemsChanged);

		// speed up facet creation by kicking off async fetching of revit categories (will be used by FacetRvtCategories)
		DtConstants.getRevitCategories();
		DtConstants.getClassificationsLibrary();
		this.viewId = undefined;

		this.lastFacetRecalcTime = 0;
		this.pendingRecalcModels = new Set();
		this.pendingAddModels = new Set();

		// manually hidden/isolated elements as tracked in onIsolationChanged and honored by updateIsolation
		this.hidden = new Map();
		this.isolated = new Map();
	}

	/**
	 * Update FacetsManager settings
	 * @param newSettings list of facet settings for base context, or list of settings per-scope.
	 * @param hiddenCategories
	 * @param useTopLevel
	 */
	async setSettings(newSettings, hiddenCategories, useTopLevel) {
		if (!newSettings?.length) {
			return;
		}

		// backward compatibility allowing simplified access to set the default context
		newSettings = newSettings[0]?.id ? [newSettings] : newSettings;

		let facetsAreChanging = false;
		//Check if element flag/category filter is changing
		if (typeof hiddenCategories !== "undefined") {
			if (!smallArraysEqual(this.hiddenCategories, hiddenCategories)) {
				this.hiddenCategories = hiddenCategories;
				facetsAreChanging = true;
			}
		}

		//Note that the options object is held as pointer
		//by the FacetLevels (and all other Facets) so setting the
		//option here and then recalculating the facet will update the facet counts
		if (typeof useTopLevel !== "undefined") {
			if (!!this.options.useTopLevel !== !!useTopLevel) {
				this.options.useTopLevel = useTopLevel;
				facetsAreChanging = true;
			}
		}

		const promises = [];
		this.facetsForScope.forEach((fs, scope) => {
			const settings = newSettings[scope];
			if (settings) {
				fs.facetsNeedRecalculation ||= facetsAreChanging;
				promises.push(fs.setSettings(settings));
			}
		});

		return Promise.all(promises);
	}

	setViewer(viewer) {

		if (this.viewer) {
			this.viewer.removeEventListener(av.PREF_CHANGED_EVENT, this.boundOnPreferenceChanged);
			this.viewer.removeEventListener(dte.DT_ROOM_FRAGMENT_LOADED_EVENT, this.boundOnRoomFragmentLoaded);
		}

		this.viewer = viewer;
		this.facetsEffects.setViewer(viewer);

		this.viewer.addEventListener(av.PREF_CHANGED_EVENT, this.boundOnPreferenceChanged);
		this.viewer.addEventListener(dte.DT_ROOM_FRAGMENT_LOADED_EVENT, this.boundOnRoomFragmentLoaded);
	}

	/**
	 * Sets event target for Dt events
	 * @param {EventDispatcher} eventTarget
	 */
	setEventTarget(eventTarget) {
		this.eventTarget = eventTarget;
	}

	highlightLocations(forceUpdate = false) {

		this.facetsEffects.clearSpaceOverlays();

		if (!this.facetsEffects.highlightingEnabled) {
			return;
		}

		const facetScope = this.getContext();

		//Check if we have a filter of any kind (except source models), in which case we will show the
		//building outline for context.
		let hasFilter = false;
		for (let i=1; i<facetScope.facetDefs.length; i++) {
			if (facetScope.facetDefs[i].filter.size > 0) {
				hasFilter = true;
				break;
			}
		}

		const spacesIdx = facetScope.getFacetDefIndex(FacetTypes.spaces);
		if (spacesIdx !== -1) {

			for (let model of this.models) {

				let roomFacets = facetScope.mergedFacets[spacesIdx];

				for (let i = 0; i < roomFacets.length; i++) {

					let facetProps = roomFacets[i];

					//Each room facet belongs to a certain model (since it's associated with room geometry in that model),
					//so we have to make sure the current room refers to the current model being processed.
					if (facetProps.roomSrcModel !== model) {
						continue;
					}

					// Add room overlays regardless of the model's visibility,
					// but skip selection for room elements of hidden models
					if (facetProps.selected) {
						this.facetsEffects.addSpaceFilterViz(facetProps, forceUpdate);
					}
				}
			}
		}

		const levelsIdx = facetScope.getFacetDefIndex(FacetTypes.levels);
		if (levelsIdx !== -1) {

			let levelsFacets = facetScope.mergedFacets[levelsIdx];
			let selectedLevels = [];

			for (let i = 0; i < levelsFacets.length; i++) {
				let facetProps = levelsFacets[i];
				if (facetProps.selected) {
					selectedLevels.push(facetProps);
				}
			}

			//In case a space is filtered by itself (without a level filter present)
			//we also show the building outline for context.
			if (selectedLevels.length || hasFilter) {
				for (let i = 0; i < levelsFacets.length; i++) {
					let facetProps = levelsFacets[i];
					// Add room overlays regardless of the model's visibility,
					// but skip selection for room elements of hidden models
					if (facetProps.selected) {
						this.facetsEffects.addFloorEdgeHighlight(facetProps);
					} else {
						this.facetsEffects.addFloorBoundaryViz(facetProps);
					}
				}

				//Add floor boundaries to levels that are filtered
				this.facetsEffects.addFloorplan(selectedLevels);
			}

		}
	}

	skipElement(flags, catId) {

		if (this.hiddenCategories.includes(catId)) {
			return true;
		}

		if ((flags & KeyFlags.Logical) === 0) {
			return false;
		}

		//Don't skip elements whose flags are "unknown" -- sometimes the NWD importer will classify
		//elements that have geometry as "unknown" and we don't want to ignore those for filtering purposes
		if (flags === ElementFlags.Unknown) {
			return false;
		}

		//if Logical element, include streams, levels, and generic assets since we want to show their properties.
		return flags !== ElementFlags.Stream && flags !== ElementFlags.Level && flags !== ElementFlags.GenericAsset && flags !== ElementFlags.Ticket;
	}

	async addModel(model, visibleInFilters) {
		await model.waitForLoad(visibleInFilters, true);

		await Promise.all(this.facetsForScope.map((fs) => fs.updateFacetsAttributes(fs.facetDefs, [model])));

		//Multiple callers can be waiting on this, some of which can trigger a recalculation again,
		//so we can't be sure inProgressRecalc won't be still set by someone else after waiting for it once.
		while (this.inProgressRecalc) {
			await this.inProgressRecalc;
		}

		//Note that there cannot be any async calls from this point down to the call to queueFacetRecalc below.

		this.models.push(model);
		if (visibleInFilters) {
			this.facetsForScope.forEach((fs) => fs.facetDefs[MODEL_FACET_DEFS_IDX].filter.add(model.urn()));
		}


		//Also recalculate facets for models that have external references to the newly added model
		//Currently this matters for the rooms facet and systems facets
		let toUpdate = new Set([model]);
		{
			let updateAll = model.isDefault() && this.facetsForScope.some((fs) => fs.getFacetDefIndex(FacetTypes.mepSystems) !== -1);
			for (let m of this.models) {
				if (m === model) {
					continue;
				}

				if (updateAll || m.hasExternalRefs(model.urn())) {
					toUpdate.add(m);
				}
			}
		}

		if (this.facility.modelsToAbort.has(model)) {
			return;
		}

		let isLast = this.models.length === this.facility.getModels().length;
		await this.queueFacetRecalc(toUpdate, model, isLast);

		if (this.models.length === 1) {
			this.viewer.addEventListener(av.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
			this.viewer.addEventListener(av.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnIsolationChanged);
		}

		if (isLast) {
			this.isLoaded = true;
			this.eventTarget.dispatchEvent({
				model,
				type: dte.DT_FACETS_LOADED,
			});
		}
	}

	/**
	 * Queue model facet recalculation, which happens lazily (if forced, 20% of all models are pending or
	 * 5 seconds have passed since the last recalculation)
	 * @param {Iterable.<DtModel>} toUpdate - models that require recalculation
	 * @param {DtModel} addedModel - model that was added triggering this recalculation
	 * @param {boolean} force
	 */
	async queueFacetRecalc(toUpdate, addedModel, force) {

		const now = Date.now();

		if (this.inProgressRecalc) {
			console.warn("unexpected pending recalc");
		}

		this.pendingAddModels.add(addedModel);

		for (let model of toUpdate) {
			this.pendingRecalcModels.add(model);
		}

		const timeDelta = now - this.lastFacetRecalcTime;
		const numModelsInFacility = this.facility.getModels().length;
		const tooMany = this.pendingAddModels.size / numModelsInFacility > 0.2;

		// Heuristic for the queue timeout:
		// The lower limit is 5 seconds, or the time of the last facet update (whichever is larger).
		// Add extra time for very large facilities, but dynamically, based on the load progress.
		// Example:
		//   For a facility with 100 models, add up to 5000 ms (50 ms per model).
		//   The amount of time added depends on how many models have already been loaded
		//   (as that increases the facet update time).
		//   So this adds 50 * 100 * 0.5 = 2500 ms when half of the models are loaded, for example.
		const modelsLoaded = this.models.length - this.pendingAddModels.size;
		const modelsLoadedRatio = modelsLoaded / numModelsInFacility; // 0 if none loaded, 1 if all loaded
		let queueTimeout = Math.max(
			this.lastFacetRecalcTook ?? 0,
			5000 + (50 * numModelsInFacility * modelsLoadedRatio)
		);

		if (force || tooMany || timeDelta > queueTimeout) {

			this.lastFacetRecalcTime = now;

			let toUpdate = Array.from(this.pendingRecalcModels);
			this.pendingRecalcModels.clear();

			let toAdd = Array.from(this.pendingAddModels);
			this.pendingAddModels.clear();

			//console.log("recalc facets", toUpdate.length, force, timeDelta);

			let resolve, reject;
			this.inProgressRecalc = new Promise((res, rej) => {
				resolve = res;
				reject = rej;
			});

			this.recalculateModelFacets(null, toUpdate, this.viewRestoreInProgress, this.viewRestoreInProgress).then(resolve).catch(reject);

			await this.inProgressRecalc;
			this.inProgressRecalc = null;

			for (let model of toAdd) {

				//Model could have been unloaded meanwhile
				if (!model.getData()) {
					continue;
				}

				//Load any room geometries for this newly added model.
				//We want this to happen after recalculateModelFacets(), which can itself trigger loading
				//of element geometries, that are more important than rooms (since rooms come into play only later
				//if the user interacts with the rooms filter).
				model.loadRoomFragments();
			}
		}
	}

	removeModel(model, skipUpdate) {
		this.hidden.delete(model);
		this.isolated.delete(model);

		const idx = this.models.indexOf(model);
		if (idx !== -1) {
			this.facetsForScope.forEach((fs) => fs.getFacetDefs().forEach((def) => def.clearCachedExternalDataForModel(model)));
			this.facetsEffects.invalidateEffectCaches(model);
			this.models.splice(idx, 1);

			if (!skipUpdate) {
				this.updateFacets();
			}
		}

		if (this.models.length === 0) {
			this.viewer.removeEventListener(av.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
			this.viewer.removeEventListener(av.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnIsolationChanged);
			this.facetsForScope.forEach((fs) => fs.facetsNeedRecalculation = true);
		}

		if (this.models.length < this.facility.getModels(true).length) {
			this.isLoaded = false;
		}
	}

	/**
	 * @param {[number,FacetDef[]]|null} scopeAndFacetDefs
	 * @param {DtModel[]} models
	 * @param [skipFacetsUpdate]
	 * @param [skipIsolationUpdate]
	 */
	async recalculateModelFacets(scopeAndFacetDefs, models, skipFacetsUpdate, skipIsolationUpdate) {

		let foundModels;

		if (models === this.models) {
			foundModels = models;
		} else {
			foundModels = models.filter(m => this.models.includes(m))
			if (foundModels.length === 0) {
				return;
			}
		}

		// Note: This may run before all relevant fragments are loaded.
		// The loader overrides the MESH_HIDDEN (setNodeOff) flag, which can cause issues at least for rooms.
		// That's why we complement this loop with onRoomFragmentLoaded.
		for(let model of foundModels) {
			// looping over all elements and set their visibility
			const dbId2flags = model.getData().dbId2flags;
			const dbId2catId = model.getData().dbId2catId;
			const vm = model.visibilityManager;
			const na = model.getInstanceTree().nodeAccess;
			for (let dbId = 1; dbId < dbId2flags.length; ++dbId) {
				const flags = dbId2flags[dbId];
				const catId = dbId2catId[dbId];

				const hiddenFromFilters = this.skipElement(flags, catId);
				const isLeafElement = na.getNumFragments(dbId) > 0

				if (vm && isLeafElement) {
					vm.lockNodeVisible(dbId, false, false);
					vm.setNodeOff(dbId, hiddenFromFilters);
					if (hiddenFromFilters) {
						vm.lockNodeVisible(dbId, true, false);
					}
				}
			}
		}

		let skipHighlights = false;
		if (scopeAndFacetDefs) {
			const [scope, nFacetDefs] = scopeAndFacetDefs;
			skipHighlights = scope !== FacetsScope.Context;
			await this.facetsForScope[scope].recalcModelFacets(nFacetDefs, foundModels);
		} else {
			await Promise.all(this.facetsForScope.map((fs) => fs.recalcModelFacets(null, foundModels)));
		}

		if (!skipFacetsUpdate) {
			this.updateFacets(foundModels.length === 1 ? foundModels[0].urn() : undefined, skipIsolationUpdate, false);
		}
		if (skipHighlights) {
			return;
		}

		// redo theming
		let idxCats = -1;
		let idxTandemCats = -1;
		let idxSystemClasses = -1;
		let idxMepSystems = -1;

		// note: for now, only context scope has physical elements to highlight.
		const facetDefs = this.getFacetDefs(FacetsScope.Context);
		facetDefs.forEach((facetDef, idx) => {
			if (facetDef.id === FacetTypes.categories) {
				idxCats = idx;
			} else if (facetDef.id === FacetTypes.systemClasses) {
				idxSystemClasses = idx;
			} else if (facetDef.id === FacetTypes.mepSystems) {
				idxMepSystems = idx;
			} else if(facetDef.id === FacetTypes.tandemCategories) {
				idxTandemCats = idx;
			}
		});

		if (idxMepSystems >= 0) {
			this.facetsEffects.setupHighlightBySystem();
		} else if (idxSystemClasses >= 0) {
			this.facetsEffects.setupHighlightBySystemClass();
		} else if (idxTandemCats >= 0) {
			this.facetsEffects.setupHighlightByTandemCategory();
		} else if (idxCats >= 0) {
			this.facetsEffects.setupHighlightByCategory();
		}
	}

	/**
	 *
	 * @param {import("./Facets").FacetSettings} settings
	 * @param {number} insertIndex
	 * @param scope
	 * @returns
	 */
	async addFacetDef(settings, insertIndex, scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.addFacetDef(settings, insertIndex));
	}

	setFilterMap(filterMap, mergeIntoExisting, skipUpdate) {
		// backward compatibility allowing simplified access to set the default context
		filterMap = Array.isArray(filterMap) ? filterMap : [filterMap];

		let scope;
		this.facetsForScope.forEach((fs) => {
			const filter = filterMap[fs.scope];
			if (filter) {
				fs.setFilterMap(filter, mergeIntoExisting, true);
				// limit if only one scope is specified
				scope = scope === undefined ? fs.scope : AllFacetsScopes;
			}
		});

		if (!skipUpdate) {
			this.updateFacets(null, null, null, scope);
		}
	}

	/**
	 * Generates a map of facet IDs to their corresponding filter sets.
	 *
	 * @returns {Object.<string, Set> | Object.<string, Set>[]} An object mapping each facet ID to a Set of its filter values.
	 */
	getFilterMap(scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFilterMap());
	}

	deriveFilterMapFromMergedFacets(scope = FacetsScope.Context) {
		function dfs(facet, wantWildcards) {
			if(!facet.children?.length) {
				return facet.selected ? { allSelected: true, filters: [facet.id] } : { allSelected: false, filters: [] };
			} else {
				const children = facet.children.map(child => dfs(child, wantWildcards));
				const allChildrenSelected = children.every(child => child.allSelected);
				const selected = (facet.selected || (facet.ids.size === 0 && allChildrenSelected)); // NOTE: if an inner node is empty, the UI doesn't explicitly mark it as selected
				const allSelected = selected && allChildrenSelected;

				let filters;
				if (wantWildcards && allSelected) {
					filters = [facet.id + "/*"];
				} else {
					filters = children.flatMap(child => child.filters);
					if (selected) {
						filters.push(facet.id);
					}
				}

				return { allSelected, filters };
			}
		}

		function derive(facets, facetDefs) {
			return facets.reduce((acc, facets, idx) => {
				const filters = facets.flatMap(facet => dfs(facet, facetDefs[idx].supportsWildcardFilters()).filters);
				acc[facetDefs[idx].id] = new Set(filters);
				return acc;
			}, {});
		}

		return this.scoped(scope, (fs) => derive(fs.getFacets(), fs.getFacetDefs()));
	}

	/** Removes any filter applied for the facet passed in by ID and updates facets. */
	clearFilter(id, skipUpdate, scope = FacetsScope.Context) {
		return this.scoped(
			scope,
			(fs) => fs.clearFilter(id),
			() => !skipUpdate && this.updateFacets(null, null, null, scope),
		);
	}

	/** Updates a facet's visibility in the required scopes. */
	setVisibilityById(facetId, valueId, visible, skipUpdate, scope = FacetsScope.Context) {
		return this.scoped(
			scope,
			(fs) => fs.setVisibility(fs.getFacetDefIndex(facetId), valueId, visible),
			() => !skipUpdate && this.updateFacets(facetId === FacetTypes.models ? valueId : null, false, visible, scope),
		);
	}

	/**
	 * Adjusts a model control's visibility.
	 * @param urn Model urn
	 * @param visible Visibility
	 * @param skipUpdate Skips the facet updates.
	 */
	setModelVisibility(urn, visible, skipUpdate) {
		this.scoped(AllFacetsScopes,
			(fs) => fs.setVisibility(MODEL_FACET_DEFS_IDX, urn, visible),
			() => !skipUpdate && this.updateFacets(urn, false, visible, AllFacetsScopes),
		);
	}

	resetVisibility(scope = AllFacetsScopes) {
		const modelFilter = this.facility.settings.links.filter((m) => m.on).map((m) => m.modelId);
		this.scoped(scope, fs => {
			fs.facetDefs.forEach(facetDef => facetDef.resetFilters());
			fs.facetDefs[MODEL_FACET_DEFS_IDX].filter = new Set(modelFilter);
		})

		this.updateFacets(null, null, null, scope);

		this.eventTarget.dispatchEvent({ type: dte.DT_FACETS_RESET, scope });
	}

	/**
	 * Determine whether a model-only filter is applied
	 * Eg. when every other facet is either all selected or all deselected
	 * @param {number|FacetsScope} [scope] - Facets scope to check or AllFacetsScopes.
	*/
	_modelOnlyFilter(scope = AllFacetsScopes) {
		const allDescendantsSelected = (node) => {
			if (!node.selected) return false;
			if (!node.children?.length) return true;

			return node.children.every((child) => allDescendantsSelected(child));
		}
		const hasModelOnly = (fs) => {
			for (let i = 1; i < fs.facetDefs.length; i++) {
				const modelOnly = fs.facetDefs[i].filter.size === 0 || fs.mergedFacets[i].every(allDescendantsSelected);
				if (!modelOnly) return false;
			}
			return true;
		}

		return scope === AllFacetsScopes
			? this.facetsForScope.every(hasModelOnly)
			: hasModelOnly(this.facetsForScope[scope]);
	}

	updateIsolation(urnHint) {
		// the primary model will never be unloaded but might need to be loaded initially
		// for all other models, missing elements are on-demand loaded
		// and stay around until the entire model gets unchecked, which unloads
		// all its elements.

		const modelOnly = this._modelOnlyFilter(AllFacetsScopes);
		//let t0 = Date.now();
		const selections = [];


		for (let m of this.models) {
			const urn = m.urn();
			// because facet filters interact with each other
			// this optimization only works if there are no filters
			// below the model level defined
			if (modelOnly && urnHint && urn != urnHint) {
				continue;
			}

			const showModel = this.facetsForScope.some((fs) => {
				const modelItem = fs.mergedFacets[MODEL_FACET_DEFS_IDX].find(item => item.id === m.urn());
				// model can be missing in a scope where it is not used.
				return modelItem && (modelItem.selected || modelItem.implicitlySelected);
			});
			if (!showModel) {
				if (this.facility.isModelVisible(m)) {
					m.visibilityManager.isolateNone();
					this.viewer.hideModel(m);

					const hasSpacesInFacets = this.facetsForScope.some(
						(fs) => m.hasRooms() && fs.getFacet(FacetTypes.spaces)?.some((s) => s.roomSrcModel === m));

					// Check if current model has selectable spaces in the filters
					if (!m.isPrimaryModel() && !hasSpacesInFacets) {
						// Keep model elements for highlights/hovering overlays... hoverlays
						m.unloadAllElements();
					}
				}
				continue;
			}

			if (!this.viewer.impl.modelVisible(m.id)) {
				this.viewer.showModel(m);
			}

			const hiddenIds = this.hidden.get(m);

			if (modelOnly) {
				m.loadAllElements();
				const hasIsolationOverride = this.isolated.size > 0;
				if(hasIsolationOverride) {
					const isolatedIds = this.isolated.get(m);
					if(isolatedIds) {
						m.visibilityManager.isolate(isolatedIds);
					} else {
						m.visibilityManager.setAllVisibility(false);
					}
				} else {
					m.visibilityManager.isolateNone();
				}
				if(hiddenIds) {
					m.visibilityManager.hide(hiddenIds);
				}
			} else {
				const sel = m.selector.getSelection().sort(compareNumbers);

				let ids = this.getIsolationForModel(m);

				if (ids?.length) {

					if (m.isPrimaryModel() && this.viewer.prefs.get(DtPrefs3D.GHOSTING)) {
						m.loadAllElements();
					} else {
						m.loadElements(ids);
					}

					m.visibilityManager.isolate(ids);
					if(hiddenIds) {
						m.visibilityManager.hide(hiddenIds);
					}

					const selection = sortedArrayIntersection(sel, ids);
					if (selection?.length) {
						selections.push({ model: m, selection });
					}
				} else {
					m.visibilityManager.setAllVisibility(false);
				}


			}
		}

		if (!modelOnly) this.viewer.impl.selector.setAggregateSelection(selections);

		//let t1 = Date.now();
		//console.log("facet update", t1 - t0);

		this.highlightLocations();

		this.viewer.dispatchEvent({
			type: av.AGGREGATE_ISOLATION_CHANGED_EVENT,
			isolation: this.viewer.getAggregateIsolation(),
			source: this
		});
	}

	// intersection of elements passing all filters and the manual isolation override
	getIsolationForModel(model, scope = AllFacetsScopes) {
		let { idsByModelFiltered } = scope === AllFacetsScopes ? this : this.facetsForScope[scope];
		let leafIds = idsByModelFiltered.get(model);
		if(leafIds) {
			const hasIsolationOverride = this.isolated.size > 0;
			if(hasIsolationOverride) {
				const isolatedIds = this.isolated.get(model);
				if(isolatedIds) {
					leafIds = sortedArrayIntersection(leafIds, isolatedIds);
				} else {
					leafIds = null;
				}
			}
		}
		return leafIds;
	}

	// same as above but in a map for all models
	getIsolation(scope = AllFacetsScopes) {
		let result = new Map();
		for(let model of this.models) {
			let isolation = this.getIsolationForModel(model, scope);
			if(isolation) {
				result.set(model, isolation);
			}
		}
		return result;
	}

	// Fast approximation whether the filters only let rooms/spaces pass.
	// There's edge cases where this method is reporting false negatives
	// (like a model with only rooms).
	_areOnlyRoomCategoriesEnabled() {
		// Again really a question about the state of the background context.
		const fs = this.facetsForScope[FacetsScope.Context];
		for (let i=0; i<fs.facetDefs.length; i++) {
			let facetDef = fs.facetDefs[i];
			if (facetDef.id === FacetTypes.tandemCategories) {
				if(facetDef.filter.size > 0) {
					let locationOnly = true
					for(let code of facetDef.filter) {
						if(!code.startsWith(DtConstants.TC.LocationPrefix)) {
							locationOnly = false;
							break;
						}
					}
					if(locationOnly) {
						return true;
					}
				}
			} else if (facetDef.id === FacetTypes.categories) {
				let filter = facetDef.filter;
				if (filter.size === 1) {
					if (filter.has(RC.Rooms) || filter.has(RC.MEPSpaces)) {
						return true;
					}
				} else if (filter.size === 2) {
					if (filter.has(RC.Rooms) && filter.has(RC.MEPSpaces)) {
						return true;
					}
				}
			}
		}
		return false;
	}

	//Detects if the current filter is filtering for a specific Level
	getLevelFilter() {
		// This is really a question about the state of the background context.
		const fs = this.facetsForScope[FacetsScope.Context];
		for (let i=0; i<fs.facetDefs.length; i++) {
			let facetDef = fs.facetDefs[i];
			if (facetDef.id === FacetTypes.levels) {
				let filter = facetDef.filter;
				//For the moment we only know how to handle
				//single level filters
				if (filter.size === 1) {
					let filterName = Array.from(filter)[0];
					let filteredFacet = fs.mergedFacets[i].find(f => f.id === filterName);
					return { facetDef, mergedFacet:  filteredFacet};
				}
			}
		}

		return {};
	}

	collectUnfilteredMergedFacets(scope = FacetsScope.Context) {
		const { mergedResultByScope } = this.makeMergedFacets(true);

		return scope === AllFacetsScopes ? mergedResultByScope : mergedResultByScope[scope];
	}

	updateFacets(urn, skipIsolationUpdate, visible, scope = AllFacetsScopes) {
		const idsByModelByScope = this.collectIdsByModel();

		this.idsByModelFiltered = new Map();
		const mergedFacetsByScope = [];
		for (const fs of this.facetsForScope) {
			if (scope === AllFacetsScopes || fs.scope === scope) {
				fs.updateMergedFacets(idsByModelByScope[fs.scope]);
			}
			mergeIdsByModel(this.idsByModelFiltered, fs.idsByModelFiltered);
			mergedFacetsByScope[fs.scope] = fs.mergedFacets;
		}

		const hasContext = scope === AllFacetsScopes || scope === FacetsScope.Context;
		if (this.viewer) {
			if (!skipIsolationUpdate) {
				this.updateIsolation(urn);
			} else {
				hasContext && this.highlightLocations();
			}

			hasContext && this.facetsEffects.makeRoomsOpaque(this._areOnlyRoomCategoriesEnabled());

			if (this.options.useTopLevel && hasContext) {
				let { facetDef, mergedFacet } = this.getLevelFilter();
				this.facetsEffects.sectionVerticalElements(facetDef, mergedFacet);
			}

			this.eventTarget.dispatchEvent({
				type: dte.DT_FACETS_UPDATED,
				addedModel: visible ? urn : undefined,
			});
		}

		return mergedFacetsByScope;
	}

	/** Map attributes from their hashes */
	async updateFacetsAttributes(facetDefs, models, scope = AllFacetsScopes) {
		return this.scoped(scope, (fs) => fs.updateFacetsAttributes(facetDefs, models));
	}

	getFacets(scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFacets());
	}

	getFacetDefs(scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFacetDefs());
	}

	getContext() {
		return this.facetsForScope[FacetsScope.Context];
	}

	onIsolationChanged(event) {
		// skipping events we fired ourselves
		if (event.source === this) {
			return;
		}

		if (event.type === av.AGGREGATE_ISOLATION_CHANGED_EVENT) {
			if(Array.isArray(event.isolation)) {
				this.isolated.clear();
				this.hidden.clear();
				if(event.isolation.length === 0) {
					// show all
					for (const fs of this.facetsForScope) {
						for(let i=1; i<fs.facetDefs.length; ++i) {
							fs.facetDefs[i].filter.clear();
						}
					}
					this.updateFacets(null, true);
				} else {
					for(let {model, ids} of event.isolation) {
						this.isolated.set(model, ids.sort(compareNumbers));
					}

					this.eventTarget.dispatchEvent({type: dte.DT_FACETS_UPDATED});
				}
			}
		} else if (event.type === av.AGGREGATE_HIDDEN_CHANGED_EVENT) {
			for(let {model, ids} of event.hidden) {
				if(!this.hidden.has(model)){
					this.hidden.set(model, ids);
				} else {
					const hiddenIds = this.hidden.get(model);
					this.hidden.set(model, hiddenIds.concat(ids));
				}

				this.eventTarget.dispatchEvent({type: dte.DT_FACETS_UPDATED});
			}
		}
	}

	setHiddenElements({model, ids}) {
		this.hidden.set(model, ids);
		this.updateIsolation(model.urn());
		this.eventTarget.dispatchEvent({type: dte.DT_FACETS_UPDATED});
	}
	setIsolatedElements({model, ids}) {
		this.isolated.set(model, ids.sort(compareNumbers));
		this.updateIsolation(model.urn());
		this.eventTarget.dispatchEvent({type: dte.DT_FACETS_UPDATED});
	}

	hasFilterOverrides() {
		return this.hidden.size > 0 || this.isolated.size > 0;
	}

	clearFilterOverrides() {
		this.hidden.clear();
		this.isolated.clear();
		this.updateIsolation();
	}

	getFilterOverrides() {
		return {
			hidden: this.hidden,
			isolated: this.isolated,
		}
	}

	getElementScope(flags) {
		switch (flags) {
			case ElementFlags.Ticket:
				return FacetsScope.Tickets;
			default:
				return FacetsScope.Context;
		}
	}

	onDocumentsChanged(event) {
		if (event?.deletedDocumentId?.length) {
			// When adding a custom facet on documents references, we need to make sure that document deletion updates facet state as well.
			// For now we do it locally within a browser session. In general, it might be useful to send events from the server to update all interested clients, but it looks like too much of an effort for now, given that this is an exotic edge case.
			// only do it if deleted document would affect the actual facets
			for (const fs of this.facetsForScope) {
				for (const facet of fs.mergedFacets) {
					const affectedDocument = facet.find(node => node.id === event.deletedDocumentId);
					if (affectedDocument) {
						this.recalculateModelFacets(null, this.models);
						return;
					}
				}
			}
		}
	}

	onSystemsChanged(event) {
		if (event.change.isPostProcessing) {
			// Ignore event about mapping task, wait for mutation event
			return;
		}

		for (const fs of this.facetsForScope) {
			const systemFacet = fs.getFacetDef(FacetTypes.mepSystems);
			if (!systemFacet) continue;

			for (let m of this.models) {
				systemFacet.clearCachedExternalDataForModel(m);
			}
		}

		this.recalculateModelFacets(null, this.models);
	}

	onPreferenceChanged(event) {

		if (event.name === DtPrefs3D.SELECTION_MODE) {
			this.recalculateModelFacets(null, this.models);
		}
	}

	onRoomFragmentLoaded(event) {
		const model = event.model;

		const vm = model.visibilityManager;
		if (!vm) {
			return;
		}

		if (!model.getInstanceTree()) {
			return;
		}

		// Do nothing if we haven't calculated facets for this model yet.
		if (this.idsByModelFiltered && !this.idsByModelFiltered.has(model)) {
			return;
		}

		const fragId = event.fragId;
		const fragmentList = model.getFragmentList();
		const dbId = fragmentList.fragments.fragId2dbId[fragId];

		const modelData = model.getData();
		const flags = modelData.dbId2flags[dbId];
		const catId = modelData.dbId2catId[dbId];

		const hiddenFromFilters = this.skipElement(flags, catId);

		vm.lockNodeVisible(dbId, false, false);

		vm.setNodeOff(dbId, hiddenFromFilters);

		if (fragmentList.isFragVisible(fragId)) {
			// The loader overrides fragment visibility, so if this fragment is visible,
			// we need to verify that it actually should be.
			const isolation = this.getIsolationForModel(model);
			if (isolation && !sortedArrayHas(isolation, dbId)) {
				vm.setVisibilityOnNode(dbId, false, true, true);
			}
		}

		if (hiddenFromFilters) {
			vm.lockNodeVisible(dbId, true, false);
		}
	}

	getFacetDefIndex(facetType, scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFacetDefIndex(facetType));
	}

	getFacetDef(facetType, scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFacetDef(facetType));
	}

	getFacet(facetType, scope = FacetsScope.Context) {
		return this.scoped(scope, (fs) => fs.getFacet(facetType));
	}

	collectIdsByModel(log) {
		log && console.time("collect elements");

		// Collect all relevant elements grouped by model
		let idsByModelByScope = this.facetsForScope.map(() => new Map());
		let count = 0;
		for(let model of this.models) {
			const dbId2flags = model.getData().dbId2flags;
			const dbId2catId = model.getData().dbId2catId;

			let ids = [];
			idsByModelByScope.forEach((idsByModel, scope) => idsByModel.set(model, ids[scope] = []));

			for (let dbId = 1; dbId < dbId2flags.length; ++dbId) {
				const flags = dbId2flags[dbId];
				const catId = dbId2catId[dbId];

				const hiddenFromFilters = this.skipElement(flags, catId);
				if(hiddenFromFilters) continue;

				const scope = this.getElementScope(flags);
				ids[scope].push(dbId);
				count += 1;
			}
		}
		log && console.timeEnd("collect elements");
		log && console.log("#elements:", count);

		return idsByModelByScope;
	}

	makeMergedFacets(ignoreFilter, skipFormat) {
		const log = false;
		log && console.group("makeMergedFacets");
		// console.profile("makeMergedFacets");
		log && console.time("makeMergedFacets");
		log && console.time("collect elements");

		// Collect all relevant elements grouped by model
		let idsByModelByScope = this.collectIdsByModel(log);

		const mergedResultByScope = [];
		const idsInMultipleFacetsByScope = [];
		for (const fs of this.facetsForScope) {
			const { mergedResult, idsByModel, idsInMultipleFacets } = fs.makeMergedFacets(idsByModelByScope[fs.scope], ignoreFilter, skipFormat);

			mergedResultByScope.push(mergedResult);
			idsByModelByScope[fs.scope] = idsByModel;
			idsInMultipleFacetsByScope.push(idsInMultipleFacets);
		}

		log && console.timeEnd("makeMergedFacets");
		//console.profileEnd("makeMergedFacets");
		log && console.groupEnd();

		return {
			mergedResultByScope,
			idsByModelByScope,
			idsInMultipleFacetsByScope,
			idsByModelFiltered: idsByModelByScope.reduce(mergeIdsByModel, new Map()),
		};
	}

	generateColorMap(defaultColorMaps, scope = FacetsScope.Context) {
		// handle the case for flat color map, in which case we use it directly
		const isScopedColorMap = defaultColorMaps
			&& typeof defaultColorMaps === "object"
			&& Object.values(FacetsScope).some(s => s in defaultColorMaps);

		return this.scoped(scope, fs => fs.generateColorMap(isScopedColorMap ? defaultColorMaps[fs.scope] : defaultColorMaps));
	}

	/** Helper to make scope specific computation clearer. note: if async, cbEnd will NOT wait for async completion. */
	scoped(scope, cbFacetScope, cbEnd) {
		const results = scope === AllFacetsScopes
			? this.facetsForScope.map(cbFacetScope)
			: cbFacetScope(this.facetsForScope[scope], scope);
		cbEnd?.();
		return results;
	}

	getStateForView() {
		const scopes = this.scoped(AllFacetsScopes, (fs, scope) => ({
			filters: this.deriveFilterMapFromMergedFacets(scope),
			settings: fs.getFacetDefs().map((facetDef) => facetDef.getSettings()),
		}));

		return {
			// For backward compatibility:
			// Dashboards only support top-level facets filters so we need to surface the context scope.
			filters: scopes[FacetsScope.Context].filters,
			settings: scopes[FacetsScope.Context].settings,
			scopes,
			hiddenCategories: this.hiddenCategories,
			useTopLevel: this.options.useTopLevel,
		}
	}

	static getBaseStateForView(facility) {
		const scopes = Object.values(FacetsScope);

		const byScope = scopes.map((scope) => {
			const settings = getFacetListForScope(scope, facility).map(f => f.getSettings());
			const filters = settings.reduce((filters, { id }) => {
				filters[id] = id === FacetTypes.models
					? new Set(facility.getModels().map(m => m.urn()))
					: new Set();

				return filters;
			}, {});

			return { filters, settings };
		});

		return {
			filters: byScope[FacetsScope.Context].filters,
			settings: byScope[FacetsScope.Context].settings,
			hiddenCategories: [],
			scopes: byScope,
		}
	}
}

function mergeIdsByModel(dst, src) {
	if (!src) return dst;

	for (let [model, ids] of src) {
		const merged = sortedArrayUnion(dst.get(model) ?? [], ids);
		dst.set(model, merged);
	}
	return dst;
}

export function getSettingsFromFacetsViewState(facility, facets) {
	if (facets?.scopes) {
		return facets.scopes.map((s) => s.settings);
	}

	if (facets?.settings) {
		// Legacy view, assume facets are about the default context scope.
		return [facets.settings];
	}

	// default settings for all facet scopes
	return facility.facetsManager.scoped(AllFacetsScopes,
		(_, scope) => getFacetListForScope(scope, facility).map((f) => f.getSettings()));
}

export function getFiltersFromFacetsViewState(facets) {
	if (facets?.scopes) {
		return facets.scopes.map((s) => s.filters);
	}

	if (facets?.filters) {
		// legacy view, assume facets are about the default context scope.
		return [facets.filters];
	}

	// no filters (accessing any scope will be undefined)
	return [];
}

export function normalizeFacetsViewState(facets) {
	function normalizeFilters(filters) {
		for (let key in filters) {
			let filter = filters[key] || [];
			// backward compatibility with existing views
			if(key === FacetTypes.categories) {
				// Revits native category ids are in the range of [-3M, -2M]. See https://git.autodesk.com/tandem/dt-server/blob/master/src/autodesk.com/double-trouble/schemas/res_rvtcat/CategoryIdsImpl.h
				// Tandem uses a more compact reprensentation in storage and APIs by projecting this range to [0, 1M] (tandemId = -2000000 - revitId).
				// Legacy views can contain actual revit category ids.
				filter = filter.map(catId => catId <= -2000000 ? -2000000 - catId : catId);
			}
			filters[key] = new Set(filter);
		}
	}

	// Three main scenarios to handle here:
	// 1. Top-level filters only (legacy views prior to scopes implementation)
	// 2. Scopes with filters only (scoped views)
	// 3. Top-level filters with scopes (hybrid views, hypothetical for now)
	if (!facets.filters) {
		facets.filters = {};
	} else {
		// Remove legacy camera and cutplanes if any
		let { camera, cutplanes, ...filters } = facets.filters;
		facets.filters = filters;
		normalizeFilters(facets.filters);
	}

	if (!facets.scopes) {
		// Inject scoped values into context scope while maintaining legacy structure at the top level
		// for facetsManager wide facets/config. Ex: model facets, useTopLevel, hiddenCategories.
		facets.scopes = [{ filters: facets.filters, settings: facets.settings }];
	} else {
		for (const { filters } of facets.scopes) {
			normalizeFilters(filters);
		}
	}

	return facets;
}

/** Adds a facet in the view state filters and settings. */
export function appendFacetToViewState(facility, viewFacets, scope, facetId, optValues) {
	const filters = getFiltersFromFacetsViewState(viewFacets)[scope] ?? {};
	const settings = getSettingsFromFacetsViewState(facility, viewFacets)[scope];

	if (!filters[facetId]) {
		settings.push({ id: facetId });
		filters[facetId] = new Set();
	}

	if (optValues != null) {
		for (const facetValue of optValues) {
			filters[facetId].add(facetValue);
		}
	}

	// temporarily also correct the legacy global values for backward compatibility.
	// @see FacetsManager.getStateForView
	if (scope === FacetsScope.Context) {
		viewFacets.settings = settings;
		viewFacets.filters = filters;
	}
}
